import RunQueue from 'run-queue';
import _ from 'lodash';
import moment from 'moment';

const previewableExt = [
	'csv',
	'xls',
	'xlsm',
	'xlsb',
	'xlsx',
	'xlt',
	'xltx',
	'pdf',
	'doc',
	'docm',
	'docx',
	'dot',
	'dotx',
	'wpd',
	'wps',
	'wri',
	'mml',
	'odc',
	'odf',
	'odg',
	'odi',
	'odm',
	'odp',
	'ods',
	'odt',
	'otg',
	'oth',
	'otp',
	'ots',
	'pxl',
	'sgl',
	'smf',
	'srw',
	'stc',
	'sti',
	'stw',
	'sxc',
	'sxg',
	'sxi',
	'sxm',
	'sxw',
	'vor',
	'wv2',
	'pot',
	'potx',
	'pps',
	'ppsx',
	'ppt',
	'pptx',
	'pptm',
	'rtf',
	'txt',
	'log',
];

export class FileDragService {
	constructor(Identity, Api, Theme, Cfg, $timeout) {
		'ngInject';
		// save injections
		this.Identity = Identity;
		this.Api = Api;
		this.Theme = Theme;
		this.Cfg = Cfg;
		this.$timeout = $timeout;
	}

	setController(ctrl) {
		this.ctrl = ctrl;
		ctrl.createUploadingPost = this.createUploadingPost.bind(this);
		ctrl.updateUploadProgress = this.updateUploadProgress.bind(this);
		ctrl.createUploadedPost = this.createUploadedPost.bind(this);
	}

	cleanup() {
		// remove pubnub listeners?
		if (this.channelDeregisters) {
			this.channelDeregisters.forEach(deregister => deregister());
		}
	}

	setup() {
		this.maxSaveConcurrency = 3;
		// handle pubnub messages about files in this hub
		this.channelDeregisters = [
			this.Identity.onHubMessage('file.has-preview', this._handleFilePreview.bind(this)),
			this.Identity.onHubMessage('file.has-text', this._handleFileSummary.bind(this)),
			this.Identity.onHubMessage('file.progress', this._handleFileProgress.bind(this)),
			this.Identity.onHubMessage('file.first-preview', this._handleFileFirstPreview.bind(this)),
			this.Identity.onHubMessage('file.error', this._handleFileError.bind(this)),
			this.Identity.onHubMessage('file.rate-limited', this._handleRateLimited.bind(this)),
			this.Identity.onHubMessage('file.no-text', this._handleFileNoText.bind(this)),
		];
		this.Identity.onUserMessage('file.transcription', this._handleFileTranscribed.bind(this));
	}

	_reloadPostByMsg(msg, completionFlag) {
		if (!this.ctrl || !this.ctrl.posts) {
			return;
		}
		this.ctrl.posts.some(post => {
			if (post.id <= 0) {
				return;
			}
			if (
				// post created from my own dragged file
				(post.file && post.file.attId == msg.attachmentId) ||
				// post from another user's dragged file
				(post.id && msg.postId && post.id == msg.postId) ||
				// post was edited and new attachment was added
				0 // TODO: handle this case
			) {
				if (!this.queue || this.queue.finished) {
					this.queue = new RunQueue({ maxConcurrency: this.maxSaveConcurrency });
				}
				// reload info
				this.queue.add(1, () => {
					return this.Api.get(`/v3/posts/${post.id}`, {}, { avoidLoadingBar: true }).then(updatedPost => {
						_.extend(post, updatedPost);
						post[completionFlag] = true;
					});
				});
				// start queue if not running already
				this.queue.run();
				// break out of loop
				return true;
			}
		});
	}

	_handleFilePreview(msg) {
		console.log('file.has-preview', msg);
		this._reloadPostByMsg(msg, 'previewComplete');
	}

	_handleFileSummary(msg) {
		console.log('file.has-text', msg);
		this._reloadPostByMsg(msg, 'summaryComplete');
	}

	_handleFileProgress(msg) {
		console.log('file.progress', msg);
		this.ctrl.posts.some(post => {
			if (post.file && post.file.s3Name === msg.key) {
				if (msg.format === 'png') {
					if (!post.pngProgress) {
						post.pngProgress = {};
					}
					_.extend(post.pngProgress, msg);
				} else if (msg.format === 'json') {
					if (this.Cfg.read('DocProcessing.set_post_body') === 'none') {
						post.textProgress = null;
						return;
					}
					if (!post.textProgress) {
						post.textProgress = {};
					}
					_.extend(post.textProgress, msg);
				}
				this.$timeout(() => {});
				return true;
			}
		});
	}

	_handleFileError(msg) {
		console.log('file.error', msg);
		this.ctrl.posts.some(post => {
			if (post.file && post.file.s3Name === msg.key && msg.format === 'json') {
				post.fileError = typeof msg === 'string' || msg instanceof String ? msg : 'Error processing file';
				this.$timeout(() => {});
				return true;
			}
		});
	}

	_handleFileFirstPreview(msg) {
		console.log('file.first-preview!', msg);
		this.ctrl.posts.some(post => {
			if (post.file && post.file.s3Name == msg.key) {
				const geometry = 'pin-260.png';
				this.loadPreviewImage(post.file, msg.data, geometry).then(image => {
					_.extend(post.image, image);
					this.$timeout(() => {});
				});
				return true;
			}
		});
	}

	_handleFileNoText(msg) {
		console.log('file.no-text', msg);
		// show a temporary message on the post that lets them know there is no text
		this.ctrl.posts.some(post => {
			if (
				// post created from my own dragged file
				(post.file && post.file.attId == msg.attachmentId) ||
				// post from another user's dragged file
				(post.id && msg.postId && post.id == msg.postId) ||
				// post was edited and new attachment was added
				0 // TODO: handle this case
			) {
				post.summaryComplete = true;
				post.rich_text_body = '<em>Document contains no text.</em>';
			}
		});
	}

	_handleRateLimited(msg) {
		console.log('file.rate-limited', msg);
		this.ctrl.posts.some(post => {
			if (post.file && post.file.s3Name == msg.key) {
				post.summaryComplete = true;
				post.previewComplete = true;
				// the `posts` record is also updated on the server to set rich_text_body permanently (or until the file is reprocessed)
				post.rich_text_body = '<em>Thanks for your patience. Your file is being processed. Please check back later.</em>';
				this.$timeout(() => {});
			}
		});
	}

	loadPreviewImage(file, data, geometry = '') {
		const width = data.width;
		const height = data.height;
		const path = data.path;

		if (geometry) {
			geometry = '.' + geometry;
		}

		// encodeURIComponent shouldn't be needed here
		const attId = file.attId || file.id;
		const attName = file.s3Basename;
		const currentHubId = this.Identity.currentClient().id;
		const userId = this.Identity.user().id;
		const url = `${window.BASE_URL}/files/images/${path}${geometry}?file-preview=1&attId=${attId}&aname=${attName}&cid=${currentHubId}&uid=${userId}`;
		const preview = { url, width, height, isFilePreview: true };

		// return a promise with the data so we can avoid resizing the image area until image is loaded
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.src = url;
			img.onload = () => resolve(preview);
			img.onerror = () => reject(preview);
		});
	}

	createUploadingPost(file) {
		const post = {
			isUploading: true,
			id: -1 * _.uniqueId(),
			percent: 0,
			intel_text: '',
			rich_text_body: '',
			headline: file.name.replace(/\.\w{1,4}$/, '').replace(/[_\s]+/g, ' '),
			image: false,
			url: '',
			submitted_by: this.Identity.user(),
			published_by: this.Identity.user(),
			created_at: moment().format(),
			published_at: moment().format(),
			date_deleted: null,
			source_link: '',
			domain_name: '',
			source_name: '',
			source_author: null,
			source_date: moment(), // TODO: check if we are filtered by client intel
			is_client_intel: false,
			is_pinned: false,
			category: null,
			tags: [],
			comments: [],
			colors: {
				code: this.Theme.data().code,
				bubble: this.Theme.data().bubble,
				link: this.Theme.data().link1,
			},
			attachment_icons: [file.category],
		};
		file.post = post;
		post.file = file;
		this.ctrl.spliceInNewPost(post);
		if (this.ctrl.posts.length > 40) {
			this.ctrl.posts.pop();
		}
	}

	updateUploadProgress(file) {
		if (file.canceled) {
			this.$timeout(() => {
				const idx = this.ctrl.posts.indexOf(file.post);
				if (idx > -1) {
					this.ctrl.posts.splice(idx, 1);
				}
			}, 1500);
		} else {
			file.post.percent = file.percent;
		}
	}

	createUploadedPost(file) {
		const setPostBody = this.Cfg.read('DocProcessing.set_post_body');
		if (setPostBody === 'none') {
			// Summary not wanted. Don't show processing text.
			file.post.summaryComplete = true;
			file.post.rich_text_body = '';
		} else if (setPostBody === 'summary') {
			file.post.rich_text_body = 'Generating summary...';
		} else if (setPostBody === 'truncated') {
			file.post.rich_text_body = 'Extracting text...';
		}
		if (!previewableExt.includes(file.extension.toLowerCase())) {
			// PAS server WILL NOT be able to generate preview images
			file.post.previewComplete = true;
		}
		const newPost = {
			intel_text: file.post.intel_text,
			headline: file.name.replace(/\.\w{1,4}$/, '').replace(/[_\s]+/g, ' '),
			submitted_by_id: this.Identity.user().id,
			published_by_id: this.Identity.user().id,
			publish: true,
			rich_text_body: '',
			// TODO: set client_view if we are currently filtered to client_intel
			// But I'm not sure how to detect filter other than peek at URL
		};
		if (!this.queue || this.queue.finished) {
			this.queue = new RunQueue({ maxConcurrency: this.maxSaveConcurrency });
		}
		this.queue.add(1, () => {
			return this.Api.create('/posts', newPost, { avoidLoadingBar: true }).then(newPostId => {
				if (!newPostId) {
					console.error('Error creating post with data', newPost, 'from file', file);
					return;
				}
				file.post.id = newPostId;
				const attData = {
					attachment_id: file.attId,
					name: file.name,
					size: file.size,
					created_at: file.date,
				};
				return this.Api.post(`/posts/${newPostId}/attachments`, attData, {
					avoidLoadingBar: true,
				}).then(() => {
					// wait until the new attachment is connected, then update post
					this.Api.get(`/v3/posts/${newPostId}`, {}, { avoidLoadingBar: true }).then(updatedPost => {
						_.extend(file.post, updatedPost);
					});
					return newPostId;
				});
			});
		});
		this.queue.run();
	}

	_handleFileTranscribed(msg) {
		console.log('file.transcribed', msg);
		this.ctrl.posts.some(post => {
			if (
				// post created from my own dragged file
				(post.file && post.file.attId == msg.attachmentId) ||
				// post from another user's dragged file
				(post.id && msg.postId && post.id == msg.postId) ||
				// post was edited and new attachment was added
				0 // TODO: handle this case
			) {
				post.transcriptionComplete = true;
				this._reloadPostByMsg(msg, 'transcriptionComplete');
			}
		});
	}
}
